import { supabase } from '../lib/supabase';

export const AUDIT_STATUSES = [
  ['WORKING', 'Working', 'working_count', 'bg-green-50 text-green-800'],
  ['REDIRECT', 'Redirect', 'redirect_count', 'bg-blue-50 text-blue-800'],
  ['CHECK_ERROR', 'Check Error', 'check_error_count', 'bg-amber-50 text-amber-800'],
  ['BROKEN', 'Broken', 'broken_count', 'bg-red-50 text-red-800'],
  ['SERVER_ERROR', 'Server Error', 'server_error_count', 'bg-red-50 text-red-800'],
  ['TIMEOUT', 'Timeout', 'timeout_count', 'bg-amber-50 text-amber-800'],
  ['WRONG_DOMAIN', 'Wrong Domain', 'wrong_domain_count', 'bg-orange-50 text-orange-800'],
  ['NO_DESTINATION', 'No Destination', 'no_destination_count', 'bg-gray-100 text-gray-700'],
];
export const isAuditActive = run => ['fetching', 'checking'].includes(run?.status);

export async function auditRequest(action, profileId, accountId, runId, signal, rowAction = null) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expired. Sign in again.');
  const base = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
  const requestController = new AbortController();
  const cancel = () => requestController.abort();
  signal?.addEventListener('abort', cancel, { once: true });
  if (signal?.aborted) cancel();
  const timer = setTimeout(cancel, 60000);
  try {
    const response = await fetch(`${base}/pinterest-audit`, {
      method: 'POST', signal: requestController.signal,
      headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, profileId, accountId, runId, ...(rowAction || {}) }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.run) throw new Error(data.error || 'Audit request failed. Resume to continue.');
    return rowAction ? data : data.run;
  } catch (error) {
    if (requestController.signal.aborted && !signal?.aborted) throw new Error('The audit request timed out. Resume to continue from saved progress.');
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

export async function loadAuditAccounts(userId, profileId, signal) {
  if (!userId || !profileId) return [];
  const { data, error } = await supabase.from('pinterest_accounts').select('id, username, profile_id')
    .eq('user_id', userId).eq('profile_id', profileId).order('created_at', { ascending: false }).abortSignal(signal);
  if (error) throw error;
  return data || [];
}

export async function loadAuditHistory(userId, profileId, accountId, signal) {
  const { data, error } = await supabase.from('pinterest_audit_runs').select('*')
    .eq('user_id', userId).eq('profile_id', profileId).eq('pinterest_account_id', accountId)
    .order('started_at', { ascending: false }).limit(20).abortSignal(signal);
  if (error) throw error;
  return data || [];
}

export async function loadAuditResults(runId, accountId, filter, search, page, signal) {
  let query = supabase.from('pinterest_audit_results').select('*', { count: 'exact' })
    .eq('audit_run_id', runId).eq('pinterest_account_id', accountId);
  if (filter === 'PROBLEMS') query = query.in('health_status', ['CHECK_ERROR', 'BROKEN', 'SERVER_ERROR', 'TIMEOUT', 'WRONG_DOMAIN', 'NO_DESTINATION']);
  else if (filter !== 'ALL') query = query.eq('health_status', filter);
  if (search.trim()) {
    // Quote the PostgREST value and escape LIKE wildcards; never interpolate raw filter syntax.
    const term = JSON.stringify(`%${search.trim().replace(/[\\%_]/g, '\\$&')}%`);
    query = query.or(['pin_title', 'pin_id', 'destination_url'].map(column => `${column}.ilike.${term}`).join(','));
  }
  const { data, count, error } = await query.order('pin_id').range(page * 50, page * 50 + 49).abortSignal(signal);
  if (error) throw error;
  return { rows: data || [], count: count || 0 };
}

export function destinationHref(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}
